import { readDraft } from "./onboardingDraftStorage";
import type { OnboardingDraftStep } from "./onboardingDraftStorage";
import type { SetupResponse } from "@/types/onboarding";
import type { QueryClient } from "@tanstack/react-query";

import { onboardingKeys } from "@/api/onboarding";

const DRAFT_STEPS: OnboardingDraftStep[] = [
  "styling",
  "operation",
  "components",
  "voice",
];

// Writes a fresh /setup/default ("re-map") response into the per-step caches.
//
// The wizard steps do NOT read the re-map: useVoiceData / useComponentsData and
// friends fetch GET /{siteId}/setup, the PERSISTED detail, and only consult
// their initialData fallback when the cache is empty (both set
// refetchOnMount: false). So a re-map has to push its result into those keys or
// the step renders the persisted payload — which, for a site that never saved,
// is a fully-populated object of empty strings, not null. StepIntro used to do
// this inline for styling/operation/voice and silently skipped components,
// which is why freshly resolved product-grid and offer-code values never
// appeared in the Components step.
//
// Unconditional overwrite, unlike rehydrateOnboardingCacheFromDraft below: a
// re-map is by definition newer than whatever the step was holding.
export function seedOnboardingCacheFromRemap(
  queryClient: QueryClient,
  siteId: string,
  kit: string,
  remapped: SetupResponse,
): void {
  const seeded: Partial<SetupResponse> = {};

  for (const step of DRAFT_STEPS) {
    const value = remapped[step];
    // null = the resolver had nothing for this section (e.g. components when
    // Prisma measured no card / h2 / button / badge). Leave the cache alone
    // rather than pinning an empty section over saved data.
    if (value === null || value === undefined) continue;
    queryClient.setQueryData(onboardingKeys[step](siteId, kit), value);
    seeded[step] = value;
  }

  // StepScope and MinimalMasthead read the allSteps entry directly, so it must
  // not keep serving the persisted empties. Merge rather than replace, and only
  // when an entry already exists: a /setup/default response carries meta: null
  // and a resolver-built intro, so writing it whole into the slot consumers
  // expect to hold the persisted shape would misreport both.
  if (Object.keys(seeded).length === 0) return;
  const key = onboardingKeys.allSteps(siteId, kit);
  const existing = queryClient.getQueryData<SetupResponse>(key);
  if (existing) {
    queryClient.setQueryData(key, { ...existing, ...seeded });
  }
}

// Re-seeds the per-step query cache with the re-map's generated data for every
// step the user hasn't saved since. Runs in the route beforeLoad after the
// server setup is loaded, so every entry path (reload onto styling/voice, a new
// tab, a step whose cache entry was garbage-collected) shows the re-map rather
// than the persisted brand book.
//
// "Does the server have data" is NOT the test: GET /setup force-populates every
// section, and a re-mapped site usually holds an older saved version of each —
// which is exactly what the re-map exists to replace. A step's generated entry
// is deleted when that step is saved (clearStep), so its presence already means
// "generated here, not saved here"; the timestamp only guards a save made
// elsewhere (another tab or device) after this re-map.
export function rehydrateOnboardingCacheFromDraft(
  queryClient: QueryClient,
  siteId: string,
  setupData: SetupResponse,
  kit: string,
): void {
  const draft = readDraft(siteId, kit);
  if (!draft?.generated) return;

  // Drafts predating generatedAt are dated by their last write.
  const generatedAt = draft.generatedAt ?? draft.savedAt;
  let merged: SetupResponse | null = null;

  for (const step of DRAFT_STEPS) {
    const generated = draft.generated[step];
    if (!generated) continue;
    const savedAt = Date.parse(setupData.meta?.[`${step}UpdatedAt`] ?? "");
    if (savedAt > generatedAt) continue;

    queryClient.setQueryData(onboardingKeys[step](siteId, kit), generated);
    merged = { ...(merged ?? setupData), [step]: generated };
  }

  // Keep the allSteps cache consistent so consumers reading it (and the
  // per-step initialData fallback) see the restored values too.
  if (merged) {
    queryClient.setQueryData(onboardingKeys.allSteps(siteId, kit), merged);
  }
}
